import { useEffect, useRef, useState } from 'react'
import type { RefObject } from 'react'

interface UseResponsiveFontPresetParams<SizeKey extends string> {
  desiredSize: SizeKey
  sizeMap: Record<SizeKey, string>
  containerRef: RefObject<HTMLElement>
}

const WIDTH_BUCKETS = [
  { max: 520, scale: 0.82 },
  { max: 700, scale: 0.88 },
  { max: 900, scale: 0.94 },
  { max: Infinity, scale: 1 },
]

const scaleFontValue = (value: string, scale: number) => {
  const match = value.trim().match(/^([0-9.]+)(px|rem|em)$/i)
  if (!match) return value
  const [, numericPart, unit] = match
  const numericValue = parseFloat(numericPart)
  if (Number.isNaN(numericValue)) return value

  const scaled = numericValue * scale
  const rounded = unit.toLowerCase() === 'px' ? Math.round(scaled) : parseFloat(scaled.toFixed(3))
  return `${rounded}${unit}`
}

/**
 * Returns a font size string that scales the configured preset down on smaller containers.
 * Keeps the same unit as provided in the size map.
 */
export function useResponsiveFontPreset<SizeKey extends string>({
  desiredSize,
  sizeMap,
  containerRef,
}: UseResponsiveFontPresetParams<SizeKey>) {
  const [responsiveSize, setResponsiveSize] = useState(() => sizeMap[desiredSize])
  const resizeObserverRef = useRef<ResizeObserver | null>(null)

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const updateSize = () => {
      const width = container.clientWidth
      const bucket = WIDTH_BUCKETS.find(entry => width < entry.max) || WIDTH_BUCKETS[WIDTH_BUCKETS.length - 1]
      const baseSize = sizeMap[desiredSize] || sizeMap[Object.keys(sizeMap)[0] as SizeKey]
      setResponsiveSize(scaleFontValue(baseSize, bucket.scale))
    }

    updateSize()

    if (typeof ResizeObserver !== 'undefined') {
      resizeObserverRef.current = new ResizeObserver(updateSize)
      resizeObserverRef.current.observe(container)
    } else {
      window.addEventListener('resize', updateSize, { passive: true })
    }

    return () => {
      resizeObserverRef.current?.disconnect()
      resizeObserverRef.current = null
      window.removeEventListener('resize', updateSize)
    }
  }, [containerRef, desiredSize, sizeMap])

  return responsiveSize
}
